import { messageErreur } from '@imp/shared/message-erreur';
import { filtrerRecherche } from '@imp/shared/recherche';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { BarreRecherche } from '@/components/BarreRecherche';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  ARTICLE_KIND_LABELS,
  deleteArticle,
  formatEuro,
  formatTauxPct,
  listArticles,
  listCategories,
  listTauxTva,
  listUnites,
} from '@/modules/catalogue/api';

export function PrestationsListPage() {
  const [q, setQ] = useState('');
  const articlesQ = useQuery({ queryKey: ['articles'], queryFn: listArticles });
  const unitesQ = useQuery({ queryKey: ['unites'], queryFn: listUnites });
  const tvaQ = useQuery({ queryKey: ['taux_tva'], queryFn: listTauxTva });
  const catQ = useQuery({ queryKey: ['categories'], queryFn: listCategories });
  const queryClient = useQueryClient();
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const del = useMutation({
    mutationFn: deleteArticle,
    onSuccess: () => {
      setConfirmId(null);
      void queryClient.invalidateQueries({ queryKey: ['articles'] });
    },
  });

  const uniteMap = useMemo(
    () => new Map((unitesQ.data ?? []).map((u) => [u.id, u])),
    [unitesQ.data],
  );
  const tvaMap = useMemo(() => new Map((tvaQ.data ?? []).map((t) => [t.id, t])), [tvaQ.data]);
  const catMap = useMemo(() => new Map((catQ.data ?? []).map((c) => [c.id, c])), [catQ.data]);

  const articles = useMemo(() => articlesQ.data ?? [], [articlesQ.data]);
  // Recherche universelle : on fouille tout ce que la ligne affiche.
  const filtered = useMemo(
    () =>
      filtrerRecherche(articles, q, (a) => [
        a.reference,
        a.libelle,
        ARTICLE_KIND_LABELS[a.kind] ?? a.kind,
        a.categorie_id ? catMap.get(a.categorie_id)?.nom : null,
        uniteMap.get(a.unite_id)?.code,
      ]),
    [articles, q, catMap, uniteMap],
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Prestations</h1>
          <p className="text-muted-foreground">
            Catalogue d'articles tarifés (matériaux, main d'œuvre, prestations).
          </p>
        </div>
        <Button asChild>
          <Link to="/prestations/nouveau">
            <Plus className="h-4 w-4" /> Nouvel article
          </Link>
        </Button>
      </div>

      <BarreRecherche
        valeur={q}
        onChange={setQ}
        quoi="un article"
        champs="référence, désignation, catégorie"
        resultats={filtered.length}
        total={articles.length}
      />

      {del.isError ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-2 text-sm text-destructive">
          {messageErreur(del.error)}
        </div>
      ) : null}

      {articlesQ.isLoading ? (
        <p className="text-muted-foreground">Chargement…</p>
      ) : articlesQ.isError ? (
        <p className="text-destructive">Erreur : {messageErreur(articlesQ.error)}</p>
      ) : filtered.length === 0 ? (
        <Card className="flex flex-col items-center justify-center gap-3 py-16 text-center">
          <p className="text-muted-foreground">
            {articles.length === 0 ? 'Aucun article au catalogue.' : 'Aucun résultat.'}
          </p>
          {articles.length === 0 ? (
            <Button asChild variant="outline">
              <Link to="/prestations/nouveau">
                <Plus className="h-4 w-4" /> Créer le premier article
              </Link>
            </Button>
          ) : null}
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/50 text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-4 py-3 font-medium">Référence</th>
                <th className="px-4 py-3 font-medium">Désignation</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Catégorie</th>
                <th className="px-4 py-3 font-medium">Unité</th>
                <th className="px-4 py-3 text-right font-medium">PU vente HT</th>
                <th className="px-4 py-3 font-medium">TVA</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => {
                const tva = tvaMap.get(a.taux_tva_id);
                return (
                  <tr key={a.id} className="border-b last:border-0 hover:bg-muted/40">
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                      {a.reference ?? '—'}
                    </td>
                    <td className="px-4 py-3 font-medium">
                      <Link to={`/prestations/${a.id}`} className="text-primary hover:underline">
                        {a.libelle}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {ARTICLE_KIND_LABELS[a.kind] ?? a.kind}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {a.categorie_id ? (catMap.get(a.categorie_id)?.nom ?? '—') : '—'}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {uniteMap.get(a.unite_id)?.code ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-right font-medium">{formatEuro(a.prix_vente_ht)}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {tva ? formatTauxPct(tva.taux) : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {confirmId === a.id ? (
                        <span className="inline-flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">Supprimer ?</span>
                          <Button
                            type="button"
                            size="sm"
                            variant="destructive"
                            disabled={del.isPending}
                            onClick={() => {
                              del.mutate(a.id);
                            }}
                          >
                            Oui
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setConfirmId(null);
                            }}
                          >
                            Non
                          </Button>
                        </span>
                      ) : (
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          aria-label={`Supprimer ${a.libelle}`}
                          onClick={() => {
                            setConfirmId(a.id);
                          }}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
